import { Button } from './ui/Button'
import { ArrowRight, Trophy, Users, Calendar } from 'lucide-react'

export default function HeroSection() {
  return (
    <section className="relative min-h-screen bg-gradient-to-br from-white via-mebi-yellow-50/30 to-mebi-yellow-100/50 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-mebi-yellow-200/20 to-mebi-yellow-300/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-tr from-mebi-green-200/15 to-mebi-yellow-200/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-mebi-yellow-100/10 to-transparent rounded-full" />
      </div>

      <div className="relative container mx-auto px-6 py-20 lg:py-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              {/* Brand Badge */}
              <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-mebi-yellow-200/50 shadow-sm">
                <Trophy className="w-4 h-4 text-mebi-yellow-600 mr-2" />
                <span className="text-sm font-medium text-mebi-yellow-700">Tournament Management Platform</span>
              </div>

              {/* Main Heading */}
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                  <span className="text-neutral-900">MeBi</span>
                  <br />
                  <span className="bg-gradient-to-r from-mebi-yellow-600 to-mebi-yellow-500 bg-clip-text text-transparent">
                    Platform
                  </span>
                </h1>
                <p className="text-xl lg:text-2xl text-neutral-600 font-light leading-relaxed max-w-lg">
                  Nền tảng quản lý giải đấu chuyên nghiệp cho các môn thể thao cơ
                </p>
              </div>

              {/* Key Features */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-white/20">
                  <div className="w-10 h-10 bg-mebi-yellow-500 rounded-lg flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-800 text-sm">Giải đấu</p>
                    <p className="text-xs text-neutral-600">Chuyên nghiệp</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-white/20">
                  <div className="w-10 h-10 bg-mebi-green-500 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-800 text-sm">Quản lý</p>
                    <p className="text-xs text-neutral-600">Thành viên</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-white/20">
                  <div className="w-10 h-10 bg-mebi-blue-500 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-800 text-sm">Lịch thi đấu</p>
                    <p className="text-xs text-neutral-600">Thời gian thực</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  variant="primary"
                  size="lg" 
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                >
                  Tải ứng dụng ngay
                </Button>
                <Button 
                  variant="secondary" 
                  size="lg"
                >
                  Tìm hiểu thêm
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center space-x-6 pt-8 border-t border-neutral-200/50">
                <div className="text-center">
                  <p className="text-2xl font-bold text-neutral-800">500+</p>
                  <p className="text-sm text-neutral-600">Thành viên</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-neutral-800">50+</p>
                  <p className="text-sm text-neutral-600">Giải đấu</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-neutral-800">24/7</p>
                  <p className="text-sm text-neutral-600">Hỗ trợ</p>
                </div>
              </div>
            </div>

            {/* Right Column - Visual */}
            <div className="relative lg:block hidden">
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-neutral-800">Giải đấu hiện tại</h3>
                      <div className="w-3 h-3 bg-mebi-green-500 rounded-full animate-pulse" />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-mebi-yellow-50 rounded-xl">
                        <div>
                          <p className="font-semibold text-neutral-800">Giải Vô địch Quốc gia</p>
                          <p className="text-sm text-neutral-600">Thể thao cơ</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-mebi-yellow-600">Đang diễn ra</p>
                          <p className="text-xs text-neutral-500">32 thí sinh</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl">
                        <div>
                          <p className="font-semibold text-neutral-800">Giải Mở rộng TP.HCM</p>
                          <p className="text-sm text-neutral-600">Thể thao cơ</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-neutral-600">Sắp diễn ra</p>
                          <p className="text-xs text-neutral-500">28 thí sinh</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-mebi-yellow-500 rounded-2xl flex items-center justify-center shadow-lg transform rotate-12 hover:rotate-0 transition-transform duration-300">
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-mebi-green-500 rounded-xl flex items-center justify-center shadow-lg transform -rotate-12 hover:rotate-0 transition-transform duration-300">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}